import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Button from "react-bootstrap/Button";
import { useMemo } from "react";
import { CardCompanyDetails } from "./CardCompanyDetails";
import { useFullScreen } from "../contexts/FullScreenProvider";
import { useSelector } from "react-redux";
import { mixpanelTrack } from "shared/trackEvents/mixpanelTracking";
import { isOpenRemunerationPlan } from "shared/plans";
import { POWER_SEARCH_EVENT_NAMES } from "shared/trackEvents/constants";

export const CardCompanyInfo = ({ id, name, allMemberships }) => {
  const { isFullScreen } = useFullScreen();
  const currentUser = useSelector((state) => state.users.user);
  // Get primary membership (current role or most recent)
  const currentMemberships = allMemberships?.filter((m) => !m.end_date) || [];
  const primaryMembership = currentMemberships[0] || allMemberships?.[0];

  // Get other memberships for modal
  const otherMemberships =
    allMemberships?.filter((m) => m !== primaryMembership) || [];

  // Group memberships by institution type
  const groupedMemberships = otherMemberships.reduce((acc, membership) => {
    const type = membership.institution_type || "Other";
    if (!acc[type]) acc[type] = [];
    acc[type].push(membership);
    return acc;
  }, {});

  // Sort memberships by start date (most recent first)
  Object.keys(groupedMemberships).forEach((type) => {
    groupedMemberships[type].sort(
      (a, b) => new Date(b.start_date) - new Date(a.start_date)
    );
  });

  const displayableMemberships = otherMemberships || [];

  // Group by year
  const groupedByYear = useMemo(
    () =>
      displayableMemberships.reduce((acc, role) => {
        const year = new Date(role.start_date).getFullYear();
        if (!acc[year]) acc[year] = [];
        acc[year].push(role);
        return acc;
      }, {}),
    [displayableMemberships]
  );

  if (otherMemberships.length === 0) {
    return (
      <div className="info-item d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center flex-grow-1">
          <i className="fas fa-briefcase mr-2" style={{ fontSize: "12px" }} />
          <span
            className="info-text"
            style={{ fontSize: "0.85rem", color: "#495057" }}
          >
            No other history
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="info-item d-flex align-items-center justify-content-between mb-2">
      <div className="d-flex align-items-center flex-grow-1">
        <i className="fas fa-briefcase mr-2" style={{ fontSize: "12px" }} />
        <span style={{ fontSize: "0.85rem" }}>Careers: </span>{" "}
        <span
          className="info-text"
          style={{ fontSize: "0.85rem", color: "#495057" }}
        >
          <OverlayTrigger
            trigger={["click"]}
            placement="auto"
            rootClose
            overlay={
              <Popover
                id={`popover-history-${id}`}
                className={isFullScreen ? "popover-fullscreen" : ""}
                style={{ maxWidth: "400px", maxHeight: "500px" }}
              >
                <Popover.Title as="h3">
                  <i className="fa fa-briefcase mr-2" />
                  {name} - Career History
                </Popover.Title>
                <Popover.Content
                  style={{
                    maxHeight: "400px",
                    overflowY: "auto",
                    padding: "12px",
                  }}
                >
                  <div className="timeline-by-year">
                    {Object.entries(groupedByYear)
                      .sort(([a], [b]) => b - a) // Sort years descending (newest first)
                      .map(([year, roles], idx) => (
                        <div
                          key={`${year}-${idx}`}
                          className="mb-3 pb-2 border-bottom border-2 border-secondary-subtle"
                        >
                          <div
                            key={`${year}-${idx}`}
                            className="fw-bold text-primary fs-6"
                          >
                            {year}
                          </div>
                          <ul
                            key={`${year}-${idx}`}
                            className="list-unstyled ps-2"
                          >
                            {roles.map((role, idx) => (
                              <CardCompanyDetails
                                key={`${role.id}-${idx}`}
                                role={role}
                                idx={idx}
                                dataLength={roles.length}
                              />
                            ))}
                          </ul>
                        </div>
                      ))}
                  </div>
                </Popover.Content>
              </Popover>
            }
          >
            <Button
              variant="link"
              size="sm"
              className="p-0 ml-1 more-companies-btn"
              style={{
                fontSize: "0.8em",
                color: "#17a2b8",
                whiteSpace: "nowrap",
                fontWeight: "600",
                textDecoration: "underline",
              }}
              onClick={() => {
                // Track career history click
                const eventName = isOpenRemunerationPlan(currentUser)
                  ? POWER_SEARCH_EVENT_NAMES.OR_POWER_SEARCH_PROFILE_CAREER_HISTORY_CLICKED
                  : POWER_SEARCH_EVENT_NAMES.OD_POWER_SEARCH_PROFILE_CAREER_HISTORY_CLICKED;

                mixpanelTrack(eventName, {
                  userEmail: currentUser?.email,
                });
              }}
            >
              View + {otherMemberships.length} more
            </Button>
          </OverlayTrigger>
        </span>
      </div>
    </div>
  );
};
